import { IconButton, Typography } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateWaterfallChartStyleOptions } from '../../../redux/ChartPoperties/ChartControlsActions';
import ChartColors from './ChartColors';
import useCombinedColorSchemes from '../../../hooks/useCombinedColorSchemes';
import { ColorSchemes } from './ColorScheme';
import { RootState } from '../../../redux';
import { OrientationRadioGroup } from '../Legend/LegendComponents/OrientationRadioGroup';
import CustomColorBuilder from './CustomColorBuilder';
import ColorPickerPopover from './ChartColorComponents/ColorPickerPopover';

const WaterfallColorControls = ({ iconColor }: { iconColor?: string }) => {
  const dispatch = useDispatch();

  const { chartControls, tabTileProps } = useSelector((state: RootState) => {
    return {
      chartControls: state.chartControls,
      tabTileProps: state.tabTileProps,
      chartProperties: state.chartProperties,
    };
  });
  const propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;

  const currentControls = chartControls.properties[propKey].waterfallChartControls || {};
  const { manualColor, positiveColor, negativeColor, totalColor, nodeValuesColors } =
    currentControls;

  const manualKeys = Object.keys(nodeValuesColors || {});
  const customList = manualKeys.filter((key) => key !== 'null');

  const allThemes = useCombinedColorSchemes();
  const currentSchemeName = chartControls.properties[propKey].colorScheme;

  const selectedScheme = allThemes.find((scheme) => scheme.name === currentSchemeName);
  const themeColors = selectedScheme?.colors || ColorSchemes[0].colors;

  const updateStyleOptions = (propKey: string, option: string, value: any) => {
    dispatch(updateWaterfallChartStyleOptions(propKey, option, value));
  };

  const lastManualSchemeRef = useRef(currentSchemeName);

  useEffect(() => {
    if (manualColor) {
      lastManualSchemeRef.current = currentSchemeName;
    }
  }, [manualColor, currentSchemeName]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeColorType, setActiveColorType] = useState<string>('positive');
  const [selectedSeriesName, setSelectedSeriesName] = useState('');
  const [selectedColor, setSelectedColor] = useState('');

  const handleColorChange = (color: string) => {
    if (activeColorType === 'positive') {
      updateStyleOptions(propKey, 'positiveColor', color);
    } else if (activeColorType === 'negative') {
      updateStyleOptions(propKey, 'negativeColor', color);
    } else if (activeColorType === 'total') {
      updateStyleOptions(propKey, 'totalColor', color);
    } else if (activeColorType === 'specific') {
      const currentMap = nodeValuesColors || {};
      const newMap = { ...currentMap, [selectedSeriesName]: color };
      updateStyleOptions(propKey, 'nodeValuesColors', newMap);
    }
  };

  const handleResetBaseColor = (type: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (type === 'positive') updateStyleOptions(propKey, 'positiveColor', '');
    if (type === 'negative') updateStyleOptions(propKey, 'negativeColor', '');
    if (type === 'total') updateStyleOptions(propKey, 'totalColor', '');
  };

  const handleAddCustomColor = (name: string, color: string) => {
    const currentMap = nodeValuesColors || {};
    const newMap = { ...currentMap, [name]: color };
    updateStyleOptions(propKey, 'nodeValuesColors', newMap);
  };

  const handleRemoveColorRule = (name: string) => {
    const currentMap = { ...(nodeValuesColors || {}) };
    delete currentMap[name];
    updateStyleOptions(propKey, 'nodeValuesColors', currentMap);
  };

  const handleRemoveAllCustomColors = () => {
    const currentMap = { ...(nodeValuesColors || {}) };
    customList.forEach((key) => {
      delete currentMap[key];
    });
    updateStyleOptions(propKey, 'nodeValuesColors', currentMap);
  };

  const renderBaseColorRow = (
    label: string,
    type: 'positive' | 'negative' | 'total',
    defaultColor: string,
  ) => {
    let displayColor = defaultColor;
    let isSet = false;

    if (type === 'positive' && positiveColor) {
      displayColor = positiveColor;
      isSet = true;
    }
    if (type === 'negative' && negativeColor) {
      displayColor = negativeColor;
      isSet = true;
    }
    if (type === 'total' && totalColor) {
      displayColor = totalColor;
      isSet = true;
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 8, width: '100%' }}>
        <div
          style={{
            height: '1rem',
            width: '30px',
            minWidth: '30px',
            backgroundColor: displayColor,
            border: '1px solid darkgray',
            cursor: 'pointer',
            marginRight: '10px',
            borderRadius: '2px',
          }}
          onClick={(e) => {
            setActiveColorType(type);
            setSelectedColor(displayColor);
            setAnchorEl(e.currentTarget);
          }}
        />
        <label
          style={{
            fontSize: '12px',
            marginLeft: '5px',
            cursor: 'default',
            flexGrow: 1,
            textAlign: 'left',
            color: iconColor ? iconColor : '#404040',
          }}
        >
          {label}
        </label>

        {isSet && (
          <IconButton
            size="small"
            onClick={(e) => handleResetBaseColor(type, e)}
            sx={{
              padding: '2px',
              marginLeft: '5px',
              color: iconColor || 'lightgray',
              '&:hover': {
                color: iconColor || 'gray',
                backgroundColor: 'transparent',
              },
            }}
            title="Reset to Default"
          >
            <RestartAltIcon style={{ fontSize: 14 }} />
          </IconButton>
        )}
      </div>
    );
  };

  const renderControls = () => (
    <div style={{ marginBottom: '10px', paddingRight: '10px' }}>
      <div
        style={{
          marginBottom: '15px',
          borderBottom: '1px dashed #eee',
          padding: '10px 10px 10px',
          textAlign: 'left',
        }}
      >
        <Typography variant="caption" style={{ color: iconColor || '#888', fontWeight: 600 }}>
          Base Colors
        </Typography>
        {renderBaseColorRow('Increase (Positive)', 'positive', themeColors[0])}
        {renderBaseColorRow('Decrease (Negative)', 'negative', themeColors[1])}
        {renderBaseColorRow('Total', 'total', themeColors[2] || themeColors[0])}
      </div>

      <CustomColorBuilder
        onAdd={handleAddCustomColor}
        existingColors={nodeValuesColors || {}}
        onRemove={handleRemoveColorRule}
        onRemoveAll={handleRemoveAllCustomColors}
        customList={customList}
        colorMap={nodeValuesColors || {}}
        colorPalette={themeColors}
        onColorChange={handleAddCustomColor}
        iconColor={iconColor}
      />
    </div>
  );

  return (
    <div style={{ marginTop: '20px', paddingTop: '8px', paddingLeft: '4px' }}>
      <OrientationRadioGroup
        orientation={manualColor ? 'manual' : 'auto'}
        options={[
          { name: 'Auto', key: 'auto' },
          { name: 'Manual', key: 'manual' },
        ]}
        onChange={(key) => {
          const isSwitchingToManual = key === 'manual';

          if (isSwitchingToManual) {
            //  if scheme changed since we were last in manual
            if (currentSchemeName !== lastManualSchemeRef.current) {
              // Waterfall has specific keys, so we wipe them individually
              updateStyleOptions(propKey, 'positiveColor', '');
              updateStyleOptions(propKey, 'negativeColor', '');
              updateStyleOptions(propKey, 'totalColor', '');
            }
            updateStyleOptions(propKey, 'manualColor', true);
          } else {
            //  Just switching mode.
            updateStyleOptions(propKey, 'manualColor', false);
          }
        }}
        style={{ width: '94%' }}
      />

      <div>
        {manualColor ? (
          <>
            {renderControls()}

            <ColorPickerPopover
              isOpen={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              currentColor={selectedColor}
              onColorChange={handleColorChange}
              propKey={propKey}
              selectedMenu=""
            />
          </>
        ) : (
          <div style={{ marginTop: '-15px' }}>
            <ChartColors iconColor={iconColor} />
          </div>
        )}
      </div>
    </div>
  );
};

export default WaterfallColorControls;
